<script>
	import { onMount } from "svelte";
	import { fly } from "svelte/transition";
	import { scaleOrdinal, scaleLinear, scaleQuantize, scaleThreshold } from "d3-scale";
	import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
	import centerOfMass from "@turf/center-of-mass";
	import nearestPointOnLine from "@turf/nearest-point-on-line";

	import Buttons from "./components/Buttons.svelte";
	import Search from "./components/Search/Search.svelte";
	import InfoBox from "./components/InfoBox.svelte";
	import MediaQuery from "./components/MediaQuerySsr.svelte";
	import Map from "./map/map";

	import { loadCSV, loadJSON, loadMapbox, loadTopojson, scrollTo, convertToGeoJSON, getStripes } from "./utils";
	import { formatInt, formatFloat } from "./language";

	// ///////////////////////////////////////////////////////////
	// props
	// ///////////////////////////////////////////////////////////
	export let name;
	export let paywall = false;

	// ///////////////////////////////////////////////////////////
	// media query variables
	// ///////////////////////////////////////////////////////////
	let breakpoint;
	let platform;
	let orientation;
	let mobileL;

	// ///////////////////////////////////////////////////////////
	// constants and definitions
	// ///////////////////////////////////////////////////////////
	let map;
	let tooltip;

	let mapZoom;

	let mapActivated = false;

	let mapLoaded = paywall;

	let labelsLorsId = "labels-plr";
	let labelsLors = [];
	let labelsBezirke = [];

	// const initialSelectedPoint = { lng: 13.388198546620572, lat: 52.55289914372827 }; // Gesundbrunnen
	const initialSelectedPoint = { lng: 13.353885860301887, lat: 52.49948919678777 }; // Nolli

	const initialBoundsBase = {
		desktop: [
			[13.252434278943213, 52.45377600350841],
			[13.524849904352578, 52.56842088538173],
		],
		tablet: [
			[13.263409296943507, 52.438914381442544],
			[13.53582492235276, 52.58204796209597],
		],
		mobile: [
			[13.24471133800381, 52.39902174203834],
			[13.51712696341258, 52.59314706316118],
		],
	};

	const searchBounds = [
		[13.761167660223435, 52.676262238895134],
		[13.073611526282605, 52.33488609760633],
	];

	$: initialBounds = initialBoundsBase[platform ? platform : "desktop"];

	const maxBounds = [
		[12.758595905118852, 52.227746928806766],
		[14.076183281386392, 52.782315046335015],
	];

	// const baseOpacity = 0.7;
	const baseOpacity = ["step", ["zoom"], 0.9, 12.5, 0.7];
	const baseOpacityLine = ["step", ["zoom"], 1, 12.5, 0.7];
	const baseOpacityLuftbilder = 0.4;

	const colorNoData = "#b9b9b9";
	const labelNoData = "keine Daten";
	let highlightingColor = "#000";

	let showLuftbilder = false;
	const luftbilderId = "luftbilder";
	const luftbilderButtonZoom = 11;

	let choroplethOpacity = baseOpacity;

	let geoBelastungen;
	let topoBelastungen;
	let dataBelastungen;
	let geoPlr;
	let dataParkplaetze;
	let geoBezirke;
	let geoRing;
	let geoStreetsParts;
	let geoStreets;

	const geoData = {
		plr: {
			id: "plr",
			nameKey: "name",
			idKey: "id",
			type: "polygon",
		},
		streets_parts: {
			id: "streets_parts",
			// nameKey: "name",
			idKey: "id",
			type: "lines",
		},
		streets: {
			id: "streets",
			nameKey: "name",
			idKey: "name",
			type: "lines",
		},
	};

	const bezirke = {
		"01": "Mitte",
		"02": "Tempelhof-Schöneberg",
		"03": "Pankow",
		"04": "Charlottenburg-Wilmersdorf",
		"05": "Spandau",
		"06": "Steglitz-Zehlendorf",
		"07": "Tempelhof-Schöneberg",
		"08": "Neukölln",
		"09": "Treptow-Köpenick",
		"10": "Marzahn-Hellersdorf",
		"11": "Lichtenberg",
		"12": "Reinickendorf",
	};

	const markerData = [{ n: "fernsehturm", lat: 52.52084869043665, lon: 13.409393064444167 }];

	const labels = [
		{ name: "Tiergarten", lnglat: [13.3585568954846, 52.513688070787865] },
		{ name: "Tempelhofer\nFeld", lnglat: [13.401950978496332, 52.474889516874036] },
		// { name: "Treptower\nPark", lnglat: [13.469671389415147, 52.4884280182969] },
		{ name: "Ernst-Reuter-\nPlatz", lnglat: [13.32191213215502, 52.51265947542748] },
		{ name: "Kottbusser\nTor", lnglat: [13.4183779004331, 52.49894850768577] },
		{ name: "Volkspark\nFriedrichshain", lnglat: [13.435708888865348, 52.52765340574239] },
		{ name: "Rathaus\nSchöneberg", lnglat: [13.343829616458176, 52.48492505227718] },
	];

	const options = [
		{
			id: "plr",
			geo: geoData.plr,
			legendLabel: "Anteil Parkplätze am Straßenraum",
			legendType: "continuous-bins",
			choroplethKey: "anteil_der_pp_flaeche_im_straßenraum_an_straßenverkehrsflaeche_in_%",
			// icon: "gruenfl",
			choroplethColorScale: scaleQuantize(),
			choroplethColorScaleText: scaleQuantize(),
			choroplethValues: [
				{ value: 0, color: "#fff39c", colorText: "#2a2a2a" },
				{ value: null, color: "#ffd766", colorText: "#2a2a2a" },
				{ value: null, color: "#FFA245", colorText: "#2a2a2a" },
				{ value: null, color: "#E26033", colorText: "#ffdec7" },
				{ value: null, color: "#B92E09", colorText: "#ffdec7" },
				{ value: 30, color: "#84000D", colorText: "#ffdec7" },
			],
			buttonData: {
				value: "plr",
				label: "Kieze",
				labelMobile: "Daten nach Kiez",
				color: "#FF9150",
				icon: "icons_nach_kiezen.png",
			},
		},
		{
			id: "streets",
			geo: geoData.streets_parts,
			legendLabel: "Parkplätze pro 100 Meter Straße",
			legendType: "continuous-bins",
			choroplethKey: "carsPerLength",
			// icon: "gruenfl",
			choroplethColorScale: scaleThreshold(),
			choroplethColorScaleText: scaleThreshold(),
			choroplethValues: [
				{ value: 0, color: "transparent", colorText: "#2a2a2a", label: "0" },
				{ value: 0.01, color: "#fff39c", colorText: "#2a2a2a", label: "1" },
				{ value: 0.1, color: "#ffd766", colorText: "#2a2a2a", label: "10" },
				{ value: 0.2, color: "#FFA245", colorText: "#2a2a2a", label: "20" },
				{ value: 0.3, color: "#E26033", colorText: "#ffdec7", label: "30" },
				{ value: 0.4, color: "#B92E09", colorText: "#ffdec7", label: "40" },
				{ value: 0.5, color: "#84000D", colorText: "#ffdec7", label: "50" },
			],
			buttonData: {
				value: "streets",
				label: "Straßen",
				labelMobile: "Daten nach Straße",
				color: "#FF9150",
				icon: "icons_nach_strassen.png",
			},
		},
	];

	const buttonOptions = options.map((o) => o.buttonData);

	const optionsById = {};
	options.forEach((e) => {
		optionsById[e.id] = e;
	});

	// const socialStatus = {
	// 	id: "social_status",
	// 	key: "soz_index",
	// 	label: "Sozialstatus",
	// 	// infobox:
	// 	// 	"Der Sozial&shy;status eines Kiezes errechnet sich aus den Indi&shy;katoren Arbeits&shy;losig&shy;keit, Empfang von Transfer&shy;leist&shy;ungen und Kinder&shy;armut. Grundlage für die Indi&shy;katoren ist das Monitoring Soziale Stadt&shy;ent&shy;wicklung (MSS) von 2021.",
	// 	translate: (value) => {
	// 		const mapping = {
	// 			"niedriger/sehr niedriger Statusindex": "social_low",
	// 			"mittlerer Statusindex": "social_medium",
	// 			"hoher Statusindex": "social_high",
	// 		};
	// 		return mapping[value];
	// 	},
	// 	labels: {
	// 		social_low: "niedrig",
	// 		social_medium: "mittel",
	// 		social_high: "hoch",
	// 	},
	// };

	// const assessments = {
	// 	bad: {
	// 		color: "#EA8F7B",
	// 		icon: "bad",
	// 	},
	// 	mid: {
	// 		color: "#EEB000",
	// 		icon: "mid",
	// 	},
	// 	good: {
	// 		color: "#76C175",
	// 		icon: "good",
	// 	},
	// 	null: {
	// 		color: colorNoData,
	// 		icon: null,
	// 	},
	// };
	// assessments.gering = assessments.good;
	// assessments.schlecht = assessments.bad;
	// assessments.mittel = assessments.mid;
	// assessments.hoch = assessments.bad;
	// assessments.gut = assessments.good;

	// ///////////////////////////////////////////////////////////
	// init colors
	// ///////////////////////////////////////////////////////////
	options.forEach((e) => {
		if (!e.colors) e.colors = {};
		e.colors.scale = e.choroplethColorScale
			.domain([...e.choroplethValues.map((d) => d.value).filter((d) => d !== null)])
			.range([...e.choroplethValues.map((d) => d.color)])
			.unknown(null);

		e.colors.scaleText = e.choroplethColorScaleText
			.domain([...e.choroplethValues.map((d) => d.value).filter((d) => d !== null)])
			.range([...e.choroplethValues.map((d) => d.colorText)])
			.unknown(null);
	});

	// ///////////////////////////////////////////////////////////
	// ui bound parameters
	// ///////////////////////////////////////////////////////////
	export let elemMap;

	let selectedOption = "plr";
	// let selectedSocials = ["social_low", "social_medium", "social_high"];
	let currentDataColors;
	let currentGeoData;
	let currentGeometry;
	let selectedRegion;
	let selectedPoint;
	let hoveredRegion;

	let infoboxText;
	$: {
		infoboxText =
			selectedOption === "plr"
				? "„Straßenraum” ist im Berliner Liegen&shy;schafts&shy;kataster definiert und umfasst alle Flächen, für die als Nutzung „Straßen&shy;verkehr” oder „Platz” angegeben ist. Diese Flächen umfassen alles, was man landläufig als öffentlichen Straßen&shy;raum ansehen würde, auch Geh- und Radwege."
				: "Angezeigt wird die durchschnittliche Anzahl an Parkplätzen pro Straßenlänge für die einzelnen Straßenabschnitte.";
	}

	// ///////////////////////////////////////////////////////////
	// update geo data
	// ///////////////////////////////////////////////////////////
	$: currentGeoData = optionsById[selectedOption].geo;
	$: if (mapActivated) {
		currentGeometry = currentGeoData.data;
		clearPoint();

		// currentGeometry.features.forEach((f) => {
		// 	// f.properties.filtered = selectedSocial !== "social_all" && f.properties[socialStatus.id] !== selectedSocial;
		// 	// hack: wenn alle aktiviert auch die Kieze anzeigen die keinen sozialen Status haben (gewerbegebiete zB)
		// 	// if (selectedSocials.length === 3) {
		// 	// 	f.properties.filtered = false;
		// 	// } else {
		// 	// 	f.properties.filtered = !selectedSocials.includes(f.properties[socialStatus.id]);
		// 	// }
		// 	f.properties.filtered = false;
		// });
		if (mapActivated) {
			// if (map.map.getLayer(currentGeoData.id)) {
			// 	if (currentGeoData.type === "polygon") map.setChoroplethData(currentGeoData.id, [0], {});
			// }

			if (map.map.getLayer(geoData.plr.id)) map.map.setPaintProperty(geoData.plr.id, "fill-opacity", choroplethOpacity);
			if (selectedOption !== "plr") {
				map.hideLayer(labelsLorsId);
				map.selectChoropleth(geoData.plr.id, null, true);
			}
			Object.keys(geoData).forEach((gd) => {
				if (!gd.includes(selectedOption)) {
					map.hideLayer(gd);
					map.hideLayer(gd + "_lines");
					map.hideLayer(gd + "_pattern");
				} else {
					map.showLayer(gd);
					map.showLayer(gd + "_lines");
					map.showLayer(gd + "_pattern");
				}
			});
			if (selectedOption !== "plr") {
				map.hideLayer(labelsLorsId);
				if (map.map.getLayer("bezirke_lines")) map.map.setPaintProperty("bezirke_lines", "line-color", "#777");
			} else {
				map.showLayer(labelsLorsId);
				if (map.map.getLayer("bezirke_lines")) map.map.setPaintProperty("bezirke_lines", "line-color", "#fff");
			}
		}
	}

	// ///////////////////////////////////////////////////////////
	// update data colors
	// ///////////////////////////////////////////////////////////
	const getDataColors = (option) => {
		if (!optionsById[option].colors.data) {
			optionsById[option].colors.data = currentGeometry.features.reduce(
				(o, f) => ({
					...o,
					[f.properties[currentGeoData.idKey]]: optionsById[option].colors.scale(
						f.properties[optionsById[option].choroplethKey]
					),
				}),
				{}
			);
		}
		return optionsById[option].colors.data;
	};

	$: if (mapActivated && currentGeoData.type === "polygon") {
		currentDataColors = getDataColors(selectedOption);

		if (mapActivated) {
			if (map.map.getLayer(currentGeoData.id)) {
				if (currentGeoData.type === "polygon")
					map.setChoroplethData(currentGeoData.id, [0], { colors: currentDataColors });
			}
		}
	}

	// ///////////////////////////////////////////////////////////
	// set opacity
	// ///////////////////////////////////////////////////////////
	$: choroplethOpacity = showLuftbilder ? baseOpacityLuftbilder : baseOpacity;

	// ///////////////////////////////////////////////////////////
	// show/hide luftbilder
	// ///////////////////////////////////////////////////////////
	$: highlightingColor = showLuftbilder ? "#fff" : "#000";

	$: if (mapActivated) {
		if (showLuftbilder) {
			map.showLayer(luftbilderId);
		} else {
			map.hideLayer(luftbilderId);
		}
		if (map.map.getLayer(geoData.plr.id)) map.map.setPaintProperty(geoData.plr.id, "fill-opacity", choroplethOpacity);

		const idHover = `${geoData.plr.id}-hovering`;
		const idSelected = `${geoData.plr.id}-selected`;

		if (map.map.getLayer(idHover)) map.map.setPaintProperty(idHover, "line-color", highlightingColor);
		if (map.map.getLayer(idSelected)) map.map.setPaintProperty(idSelected, "line-color", highlightingColor);

		map.updateChoroplethOpts(geoData.plr.id, {
			hoveringBorderColor: highlightingColor,
			selectedBorderColor: highlightingColor,
		});
	}

	// ///////////////////////////////////////////////////////////
	// data processing
	// ///////////////////////////////////////////////////////////
	const processData = () => {
		dataParkplaetze.forEach((d) => {
			Object.entries(d).forEach(([k, v]) => {
				if (!["id", "name", "bezirk"].includes(k)) d[k] = +v;
			});
		});

		geoPlr.features.forEach((f) => {
			const id = f.properties[geoData.plr.idKey];
			const data = dataParkplaetze.find((d) => d[geoData.plr.idKey] === id);

			f.properties = { ...f.properties, ...data };

			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];
			f.properties.lon = center[0];
			f.properties.lat = center[1];

			f.properties.type = "plr";

			labelsLors.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoBezirke.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			f.properties.centerLng = center[0];
			f.properties.centerLat = center[1];

			f.properties.name = bezirke[f.properties["BEZ"]];

			labelsBezirke.push({
				name: `${f.properties.name}`,
				lnglat: [f.properties.centerLng, f.properties.centerLat],
			});
		});

		geoStreetsParts.features.forEach((f, i) => {
			f.properties.id = i;
		});

		geoStreets.features.forEach((f) => {
			const center = centerOfMass(f).geometry.coordinates;
			const point = nearestPointOnLine(f, center).geometry.coordinates;
			f.properties.centerLng = point[0];
			f.properties.centerLat = point[1];
			f.properties.type = "street";
		});
	};

	// ///////////////////////////////////////////////////////////
	// remove label for hovered/selected region
	// ///////////////////////////////////////////////////////////
	$: if (mapActivated) {
		labelsLors.forEach((l) => {
			l.opacity =
				(hoveredRegion && l.name === hoveredRegion.name) || (selectedRegion && l.name === selectedRegion.name) ? 0 : 1;
		});

		if (map.map.getLayer(labelsLorsId)) map.updateLabels(labelsLors, labelsLorsId);
	}
	// ///////////////////////////////////////////////////////////
	// initialize map
	// ///////////////////////////////////////////////////////////

	let tooltipHovered = false;

	const choroplethOpts = {
		geoType: "geojson",
		borderColor: "#FFF",
		borderWidth: 0.5,
		borderOpacity: 1,
		opacity: choroplethOpacity,
		dataType: "colors",
		colors: currentDataColors,
		data: [0],
		interactive: true,
		zoomOnClick: true,
		zoomLevel: 12.7,
		hoveringBorderColor: highlightingColor,
		hoveringBorderWidth: 3,
		selectedBorderColor: highlightingColor,
		selectedBorderWidth: 3,
		// filteredColor: "transparent",
		// filteredBorderColor: "transparent",
		choroplethClickCallback: (f, filtered, lngLat) => {
			if (selectedOption === "plr") {
				if (!filtered) selectPoint(lngLat, f);
			}
		},
		backgroundClickCallback: () => {
			if (selectedOption === "plr") {
				clearPoint();
			}
		},
		mousemoveCallback: (f, filtered) => {
			if (isTouch) return;
			if (selectedOption === "plr") {
				hoveredRegion = f.properties;
				addPopup(f);
			}
		},
		mouseleaveCallback: () => {
			if (selectedOption === "plr") {
				setTimeout(() => {
					if (!tooltipHovered) {
						hoveredRegion = undefined;
						removePopup();
						if (selectedRegion) addPopup(selectedRegion);
					}
				});
			}
		},
	};

	const lineOpts = {
		geoType: "geojson",
		opacity: baseOpacity,
		hoveringWidth: 30,
		dashed: false,
		hover: false,
	};

	let isTouch = false;

	onMount(async () => {
		if (paywall) return;

		const promises = [
			loadJSON(`${PATH}assets/data/berlin_bezirke.geo.json`),
			loadJSON(`${PATH}assets/data/ring.geo.json`),
			loadJSON(`${PATH}assets/data/planungsraeume.sim.geo.json`),
			loadCSV(`${PATH}assets/data/parkplaetze.csv`),
			loadJSON(`${PATH}assets/data/abschnitte.min.geo.json`),
			loadJSON(`${PATH}assets/data/strassen.min.geo.json`),
			loadTopojson(),
			loadMapbox(),
		];

		function checkTouch() {
			isTouch = true;
			document.querySelector("body").removeEventListener("touchstart", checkTouch);
		}
		document.querySelector("body").addEventListener("touchstart", checkTouch);

		[geoBezirke, geoRing, geoPlr, dataParkplaetze, geoStreetsParts, geoStreets] = await Promise.all(promises);

		processData();
		geoData.plr.data = geoPlr;
		geoData.streets_parts.data = geoStreetsParts;
		geoData.streets.data = geoStreets;

		currentGeometry = currentGeoData.data;
		currentDataColors = getDataColors(selectedOption);

		map = new Map(elemMap, {
			useFranklin: true,
			initialBounds: initialBounds,
			maxBounds,
			controls: true,
			controlsLeft: true,
			scale: true,
			popup: false,
			popupShift: false,
			labels: true,
			styleJson: `${PATH}assets/style.json`,
			hideBackgroundMap: false,
		});

		map.activated
			.then(() => map.loaded)
			.then(() => {
				const REFLAYER = map.firstLabelLayer;

				map.hideAttribution();

				mapZoom = map.map.getZoom();
				map.map.on("zoom", () => {
					mapZoom = map.map.getZoom();
				});

				map.map.loadImage(getStripes("#ccc", 4, "#fff"), (error, image) => {
					map.map.addImage("stripes-nodata", image);
				});

				// luftbilder
				map.map.addSource(luftbilderId, {
					type: "raster",
					url: "https://interaktiv.tagesspiegel.de/maptiles/luftbilder_2022/tiles.json",
				});

				map.map.addLayer(
					{
						id: luftbilderId,
						source: luftbilderId,
						type: "raster",
					},
					REFLAYER
				);

				// dataset for all issues
				map.addChoropleth(geoData.plr.data, {
					...choroplethOpts,
					id: geoData.plr.id,
					identifier: geoData.plr.idKey,
					colors: currentDataColors,
					noDataColor: "#fff",
					noDataPattern: "stripes-nodata",
					beforeLayer: REFLAYER,
					zoomPadding: () => {
						return {
							top: 0,
							left: 0,
							// bottom: platform === "mobile" ? 150 : 0,
							// right: platform === "mobile" ? 0 : 290,
							bottom: window.innerWidth <= 544 ? 150 : 0,
							right: window.innerWidth <= 544 ? 0 : 290,
						};
					},
				});

				map.map.addSource(geoData.streets_parts.id, {
					type: "geojson",
					data: geoData.streets_parts.data,
				});

				map.map.addLayer(
					{
						id: geoData.streets_parts.id,
						source: geoData.streets_parts.id,
						type: "line",
						layout: {
							"line-cap": "round",
							"line-join": "round",
						},
						paint: {
							"line-opacity": baseOpacityLine,
							"line-width": ["step", ["zoom"], 3, 12.5, 7, 14, 10],
							"line-color": [
								"interpolate",
								["linear"],
								["get", "carsPerLength"],
								...optionsById.streets.choroplethValues.map((v) => [v.value, v.color]).flat(),
							],
						},
					},
					REFLAYER
				);

				map.setupLines(geoData.streets.data, {
					...lineOpts,
					id: geoData.streets.id,
					identifier: geoData.streets.idKey,
					//width: 3,
					width: ["step", ["zoom"], 3, 12, 7, 14, 10],
					// colors: currentDataColors,
					opacity: 0,
					beforeLayer: REFLAYER,
					visibility: "none",
					hover: true,
					tooltip: false,
					hoverRadius: window.innerWidth <= 544 ? 8 : 8,
					hoverCallback: (f, snappedPoint, lngLat) => {
						if (currentGeoData.id.includes(geoData.streets.id)) {
							if (isTouch) return;
							hoveredRegion = f ? f.properties : undefined;
							if (f) addPopup(f, snappedPoint);
							else {
								removePopup();
								if (selectedRegion) addPopup(selectedRegion);
							}
						}
					},
					clickCallback: (f, snappedPoint, lngLat) => {
						selectPoint(snappedPoint, f);
          },
          backgroundClickCallback: () => {
            if (selectedOption === "streets") {
              clearPoint();
            }
          },
					mouseleaveCallback: () => {
						setTimeout(() => {
							if (!tooltipHovered) {
								hoveredRegion = undefined;
								removePopup();
								if (selectedRegion) addPopup(selectedRegion);
							}
						});
					},
					highlight: true,
					highlightColor: "#fff",
					highlightColorOutline: "#2a2a2a",
					highlightOutline: ["interpolate", ["linear"], ["zoom"], 10, 2, 14, 6, 16, 8],
					beforeLayerHighlight: geoData.streets_parts.id,
					zoomOnClick: true,
					zoomLevel: 12.7,
				});

				// bezirke
				map.addChoropleth(geoBezirke, {
					geoType: "geojson",
					id: "bezirke",
					borderColor: "#fff",
					// borderColor: "#9b9b9b",
					borderWidth: 3,
					interactive: false,
					opacity: 0,
					beforeLayer: REFLAYER,
				});

				// labels

				const labelsOpts = {
					minZoom: 12.5,
					maxZoom: 14.5,
					fontSize: 12,
					color: "#fff",
					haloWidth: 1,
					haloColor: "#000",
					letterSpacing: 0.015,
					font: "Franklin Gothic Medium",
				};

				map.addLabels(labelsLors, {
					...labelsOpts,
					id: labelsLorsId,
					textAllowOverlap: true,
					textAnchor: "top",
				});

				map.addLabels(labels, {
					...labelsOpts,
					id: "labels",
					minZoom: 9,
					maxZoom: 11.5,
				});

				// map.addLabels(labelsBezirke, {
				// 	...labelsOpts,
				// 	id: "labels-bezirke",
				// 	minZoom: 9,
				// 	maxZoom: 11.5,
				// });

				showBerlinMarkers();
				showRing();

				mapActivated = true;

				// requestAnimationFrame(() => {
				// 	map.map.moveLayer("water", "bezirke");
				// });

				map.map.once("idle", () => {
					mapLoaded = true;

					// preselect a point
					selectPoint(initialSelectedPoint, undefined, true);
				});
			});
	});

	// ///////////////////////////////////////////////////////////
	// map selection
	// ///////////////////////////////////////////////////////////
	let marker = null;
	let popup = null;

	const removeMarker = () => {
		if (marker) marker.remove();
	};

	const addMarker = (lon, lat) => {
		if (marker) marker.remove();
		marker = new window.mapboxgl.Marker({
			// anchor: 'bottom',
			color: "#1f1f1f",
			scale: 0.7,
		})
			.setLngLat([lon, lat])
			.addTo(map.map);
	};

	const removePopup = () => {
		if (popup) popup.remove();
	};

	const addPopup = (feature, lngLat) => {
		const props = feature.properties ? feature.properties : feature;

		if (popup) popup.remove();

		let html = "";
		if (props.type === "plr") {
			html = `
				<div class="type">Planungsraum</div>
        <div class="name">${props.name}</div>
        <div class="bezirk">in ${props.bezirk}</div>
      `;
		} else if (props.type === "street") {
			const bezirke = typeof props.bezirke === "string" ? JSON.parse(props.bezirke) : props.bezirke;
			html = `
        <div class="name">${props.name}</div>
        <div class="bezirk">in ${bezirke.join(", ")}</div>
      `;
		}

		// const cursorLocation = this.map.unproject(point).toArray();
		// 				const snappedPoint = nearestPointOnLine(feature, cursorLocation);

		popup = new window.mapboxgl.Popup({
			color: "#1f1f1f",
			scale: 0.7,
			closeButton: false,
			closeOnClick: false,
			closeOnMove: false,
			className: "region-popup",
		})
			.setLngLat(lngLat || [props.centerLng, props.centerLat])
			.setHTML(html)
			.addTo(map.map);
	};

	const findFeature = (lon, lat) => {
		let feature = null;
		// first try mapbox, only works if feature is visible
		// then search in the geometry data
		const point = map.map.project([lon, lat]);

		const fs = map.map.queryRenderedFeatures(point, {
			layers: [currentGeoData.id],
		});

		if (fs.length === 1) {
			feature = fs[0];
		} else {
			const pointf = {
				type: "Feature",
				geometry: {
					type: "Point",
					coordinates: [lon, lat],
				},
			};
			for (let i = 0; i < currentGeometry.features.length; i++) {
				try {
					if (booleanPointInPolygon(pointf, currentGeometry.features[i])) {
						feature = currentGeometry.features[i];
						break;
					}
				} catch (err) {
					console.log(err);
				}
			}
		}
		return feature;
	};

	const selectRegion = (newRegion) => {
		if (!selectedRegion || !newRegion) {
			selectedRegion = newRegion;
			if (!isTouch) hoveredRegion = newRegion;
		} else {
			const alreadySelected = newRegion === selectedRegion;

			selectedRegion = alreadySelected && !selectedPoint ? undefined : newRegion;
			if (!isTouch) hoveredRegion = selectedRegion;
		}

		if (selectedRegion && !selectedRegion.name) selectedRegion.name = selectedRegion[currentGeoData.nameKey];

		stopSearch(selectedRegion ? selectedRegion.name : false);
	};

	const clearPoint = () => {
		selectedPoint = null;
		selectedRegion = null;
		hoveredRegion = null;
		tooltip = undefined;
		removeMarker();
		removePopup();
		if (map.map.getLayer(geoData.plr.id)) map.selectChoropleth(geoData.plr.id, false, true);
		if (map.map.getLayer(geoData.streets.id)) map.selectLine(geoData.streets.id, null);
		selectRegion(false);
	};

	// feature is optional
	const selectPoint = (lngLat, optionalFeature, stayWhereYouAre) => {
		let feature; // = selectedRegion;
		let featureProperties;
		if (lngLat) {
			selectedPoint = lngLat;
			// addMarker(lngLat.lng, lngLat.lat);
		}
		if (optionalFeature) {
			feature = optionalFeature;
		} else if (lngLat) {
			feature = findFeature(lngLat.lng, lngLat.lat);
		} else if (selectedPoint) {
			feature = findFeature(selectedPoint.lng, selectedPoint.lat);
		}
		featureProperties = feature ? feature.properties : undefined;
		if (feature) {
			// D: hier habe ich das false eingefügt - man möchte via adresssuche auch auf kieze kommen die gerade gefiltert sind
			if (false && featureProperties.filtered === true) {
				clearPoint();
				map.toInitialView();
			} else if (featureProperties !== selectedRegion) {
				addPopup(feature);
				selectRegion(featureProperties);
				map.selectChoropleth(geoData.plr.id, null, true);
				map.selectChoropleth(geoData.plr.id, featureProperties.id, stayWhereYouAre);
			}
		} else {
			clearPoint();
		}
	};

	const scrollToTopOfMap = () => {
		scrollTo(elemMap, -90);
	};

	const clearSelection = () => {
		clearPoint();
		scrollToTopOfMap();
	};

	// ///////////////////////////////////////////////////////////
	// markers
	// ///////////////////////////////////////////////////////////

	const berlinMarkers = [];

	function showBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				markerData.forEach((d) => {
					const melc = document.createElement("div");
					const mel = document.createElement("div");
					melc.appendChild(mel);
					mel.classList.add("marker");
					mel.classList.add(`marker--${d.n}`);
					mel.innerHTML = d.label || "";
					berlinMarkers.push(new window.mapboxgl.Marker(melc).setLngLat([d.lon, d.lat]).addTo(map.map));
				});
			});
	}

	function hideBerlinMarkers() {
		map.activated
			.then(() => map.loaded)
			.then(() => {
				berlinMarkers.forEach((m) => {
					m.remove();
				});
			});
	}

	let ringshown = false;
	let ringshownonce = false;
	let ringmarker;
	const maxZoomRing = 13.5;
	function showRing() {
		if (ringshown) return;
		ringshown = true;
		if (!ringshownonce) {
			ringshownonce = true;
			map.activated
				.then(() => map.loaded)
				.then(() => {
					map.map.addSource("sbahnring", {
						type: "geojson",
						data: geoRing,
					});
					map.map.addLayer({
						id: "sbahnring1",
						type: "line",
						source: "sbahnring",
						maxzoom: maxZoomRing,
						layout: {},
						paint: {
							"line-color": "#ffffff",
							"line-width": 3,
						},
					});
					map.map.addLayer({
						id: "sbahnring2",
						type: "line",
						source: "sbahnring",
						maxzoom: maxZoomRing,
						layout: {},
						paint: {
							"line-color": "#1f1f1f",
							"line-width": 2,
							"line-dasharray": [1, 1],
						},
					});
					// const pos = [13.277214090669595, 52.52379218491869];
					const pos = [13.360157239162685, 52.54536032817046];
					const el = document.createElement("div");
					el.className = "ring-marker";
					el.innerHTML = '<div class="ring-marker-text">S-Bahn-Ring</div>';
					ringmarker = new window.mapboxgl.Marker(el).setLngLat(pos);
					ringmarker.addTo(map.map);
				});
		} else {
			map.map.setLayoutProperty("sbahnring1", "visibility", "visible");
			map.map.setLayoutProperty("sbahnring2", "visibility", "visible");
			ringmarker.addTo(map.map);
		}
	}

	function hideRing() {
		if (!ringshown) return;
		map.map.setLayoutProperty("sbahnring1", "visibility", "none");
		map.map.setLayoutProperty("sbahnring2", "visibility", "none");
		ringmarker.remove();
		ringshown = false;
	}

	$: if (ringmarker) ringmarker.getElement().classList.toggle("hidden", mapZoom > maxZoomRing);

	// ///////////////////////////////////////////////////////////
	// search
	// ///////////////////////////////////////////////////////////
	let stopSearch;

	// ///////////////////////////////////////////////////////////
	// tooltip
	// ///////////////////////////////////////////////////////////

	const closeClick = () => clearPoint();

	function tooltipClick() {
		// clearPoint();
	}

	let tooltipExpanded = true;

	function tooltipToggle(e) {
		if (window.innerWidth > 544) return;
		e.stopPropagation();
		tooltipExpanded = !tooltipExpanded;
	}

	function tooltipMouseover() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = true;
		if (selectedRegion) {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}
	}

	function tooltipMouseout() {
		if (window.innerWidth <= 544) return;
		tooltipHovered = false;
	}

	const frmtI = formatInt();
	const frmtF = formatFloat({ digits: 1, adaptive: false, decimal: false });
</script>

<MediaQuery bind:breakpoint bind:platform bind:orientation bind:mobileL />
<div class="gruenflaechen-map" class:pw={paywall}>
	<div class="headline">Finde deinen Kiez</div>
	<div class="ui-container-top" class:inactive={!mapLoaded}>
		<div class="legend">
			<div class="legend-header">
				<div class="legend-title">{optionsById[selectedOption].legendLabel}</div>
				<InfoBox content={infoboxText} />
			</div>
			<div class="legend-items {optionsById[selectedOption].legendType}">
				{#if optionsById[selectedOption].legendType === "separate"}
					{#each optionsById[selectedOption].choroplethValues as v}
						<div class="legend-item">
							<div class="legend-symbol" style:background={v.color} />
							<div class="legend-label">{v.value}</div>
						</div>
					{/each}
				{/if}
				{#if optionsById[selectedOption].legendType === "continuous-bins"}
					{@const bins =
						optionsById[selectedOption].id === "streets"
              ? optionsById[selectedOption].choroplethValues.map(x => x.value)
							: [
									optionsById[selectedOption].colors.scale.domain()[0],
									...optionsById[selectedOption].colors.scale.thresholds(),
							  ]}
					{#each bins as v, i}
						{@const color = optionsById[selectedOption].id === "plr" ? optionsById[selectedOption].colors.scale(v) : optionsById[selectedOption].choroplethValues[i].color }
						<div class="legend-item">
							<div class="legend-symbol" style:background={color} />
							<div class="legend-label">
								{#if optionsById[selectedOption].choroplethValues[i].label !== undefined}
									{optionsById[selectedOption].choroplethValues[i].label}
								{:else}
									{Math.round(v)}{i === bins.length - 1 && optionsById[selectedOption].id === "plr" ? "%" : ""}
								{/if}
							</div>
						</div>
					{/each}
				{/if}
			</div>
		</div>
		<div class="buttons">
			<Buttons bind:value={selectedOption} options={buttonOptions} let:option let:active heading="Karte umschalten">
				<div class="button-main" class:active>
					<img src="{PATH}assets/images/icons/{option.icon}" alt="" />
					<span class="button-d" data-title={option.label}>{option.label}</span>
					<span class="button-m" data-title={option.labelMobile}>{option.labelMobile}</span>
				</div>
			</Buttons>
		</div>
		<div class="search">
			{#if selectedOption === "plr"}
				<Search
					type={mapLoaded ? ["data", "geo"] : ["geo"]}
					icon="pin"
          dataSearchData={mapLoaded && geoData.plr.data && geoData.plr.data.features ? geoData.plr.data : {features:[]}}
					dataSearchKeys={[geoData.plr.nameKey]}
					dataSearchGetLabel={(d) => d[geoData.plr.nameKey]}
					geoSearchBounds={[searchBounds[1], searchBounds[0]]}
					placeholder="Kiez, Adresse, Ort eingeben"
					clearCallback={() => clearPoint()}
					resultCallback={(d) => {
						addMarker(d.lon, d.lat);
						const lnglat = { lng: d.lon, lat: d.lat };

						// if (selectedOption === "streets") {
						// 	map.map.flyTo({
						// 		center: [d.lon, d.lat],
						// 		zoom: Math.max(map.map.getZoom(), 15),
						// 	});
						// } else {
						selectPoint(lnglat);
						// }
					}}
					noDataFunctionGeo={(d) => (selectedOption === "streets" ? false : !findFeature(d.lon, d.lat))}
					bind:stopSearch
				/>
			{/if}
			{#if mapActivated && selectedOption === "streets"}
				<Search
					type={["data"]}
					icon="pin"
					dataSearchData={geoData.streets.data}
					dataSearchKeys={[geoData.streets.nameKey]}
					dataSearchGetLabel={(d) => d[geoData.streets.nameKey]}
					placeholder="Straße eingeben"
					clearCallback={() => clearPoint()}
					resultCallback={(d) => {
						const feature = geoData.streets.data.features.find(
							(f) => f.properties[geoData.streets.idKey] === d.datum[geoData.streets.idKey]
						);
						const lnglat = { lng: d.datum.centerLng, lat: d.datum.centerLat };
						selectPoint(lnglat, feature);
					}}
					bind:stopSearch
				/>
			{/if}
		</div>
	</div>
	<div
		class="map-container"
		class:tooltip-shown={tooltip && selectedOption !== "streets"}
		on:mouseleave={() => {
			hoveredRegion = undefined;
			removePopup();
			if (selectedRegion) addPopup(selectedRegion);
		}}
	>
		<div class="map" bind:this={elemMap} />

		{#if paywall}
			<div class="pw-map-image" />
		{/if}

		<div
			class="button-luftbilder"
			class:hidden={mapZoom < luftbilderButtonZoom && !showLuftbilder}
			class:luftbilder={showLuftbilder}
			on:click={() => {
				showLuftbilder = !showLuftbilder;
			}}
		>
			<div class="button-luftbilder-image button-luftbilder-image-on" />
			<div class="button-luftbilder-image button-luftbilder-image-off" />
			<div class="button-luftbilder-text mobile">
				{showLuftbilder ? "Straßen-karte" : "Luft-bilder"}
			</div>
			<div class="button-luftbilder-text desktop">
				{showLuftbilder ? "Auf Straßenkarte umschalten" : "Auf Luftbilder umschalten"}
			</div>
		</div>
		<div class="map-loading" class:mapLoaded>
			<div class="ts-spinner">
				<div class="ts-double-bounce1" />
				<div class="ts-double-bounce2" />
			</div>
		</div>
		{#if selectedRegion || (hoveredRegion && !hoveredRegion.filtered)}
			{@const region = hoveredRegion || selectedRegion}
			{@const hoveredAndSelectedAreSame =
				hoveredRegion && selectedRegion ? hoveredRegion.plr === selectedRegion.plr : false}
			{@const isSelected = (!hoveredRegion || hoveredAndSelectedAreSame) && !!selectedRegion}
			<!-- {@const regionSocial = region[socialStatus.id]} -->
			<div
				class="tooltip {selectedOption}"
				on:click={tooltipClick}
				on:mouseover={tooltipMouseover}
				on:focus={() => {}}
				on:blur={() => {}}
				on:mouseout={tooltipMouseout}
				class:expanded={tooltipExpanded}
				transition:fly={{ y: 100, duration: platform === "mobile" ? 400 : 0 }}
				bind:this={tooltip}
			>
				{#if selectedOption === "plr"}
					{@const choroplethValue = region[optionsById[selectedOption].choroplethKey]}
					{@const choroplethColor = optionsById[selectedOption].colors.scale(choroplethValue)}
					{@const choroplethColorText = optionsById[selectedOption].colors.scaleText(choroplethValue)}

					<div class="tooltip-top">
						<div class="title-and-bezirk">
							<div class="lor">Planungsraum</div>
							<div class="title">
								{@html region.name.replace("/", "/&#8203;")}
							</div>
							<div class="bezirk">in {region.bezirk}</div>
						</div>
						{#if isSelected}
							<div class="button-close" on:click={closeClick} />
						{/if}
					</div>
					<div class="rule" />
					<div class="row rowSelected">
						<span class="label">Anteil Parkplätze an Straßenfläche:</span>
						<span class="value value-box" style:background={choroplethColor} style:color={choroplethColorText}
							>{frmtF(region[options[0].choroplethKey])}%</span
						>
					</div>

					<div class="row">
						<span class="label">Parkplätze auf Straßenfläche:</span>
						<span class="value">{frmtI(region["pp_strassenraum_durchschnitt"])}</span>
					</div>

					<div class="row">
						<span class="label">Fläche der Parkplätze:</span>
						<span class="value"
							>{frmtI(
								(region["pp_in_qm"] / region["pp_durchschnitt"]) * region["pp_strassenraum_durchschnitt"]
							)}&thinsp;m²</span
						>
					</div>

					<div class="rule" />

					<div class="row row-text">
						Auf jeden der <b>{frmtI(region.einwohner)} Menschen</b> im Kiez kommen im Schnitt
						<b>{frmtF(region.pp_strassenraum_durchschnitt / region.einwohner)} Parkplätze</b>.
						<br />
						<b>{frmtI(region.kfz)} Kfz</b> sind im Kiez gemeldet. Auf jedes Kfz kommen im Schnitt
						<b>{frmtF(region.pp_strassenraum_durchschnitt / region.kfz)} Parkplätze</b>.
					</div>

					<div class="rule" />

					<div class="row row-text">
						<b>Parkplätze</b> auf öffentlichem Straßenland machen
						<b
							>{frmtF(
								(((region["pp_in_qm"] / region["pp_durchschnitt"]) * region["pp_strassenraum_durchschnitt"]) /
									region.flaeche_in_qm) *
									100
							)}%</b
						>
						der Kiezfläche aus, <b>Spielplätze {frmtF(region["spielplatzflaechen_in_%"])}%</b> und
						<b>Grünflächen {frmtF(region["gruenanlagenflaechen_in_%"])}%</b>.
					</div>
				{:else}
					{@const choroplethValue = region[optionsById[selectedOption].choroplethKey]}
					{@const choroplethColor = optionsById[selectedOption].colors.scale(choroplethValue)}
					{@const choroplethColorText = optionsById[selectedOption].colors.scaleText(region.carsPerLength)}

					<div class="tooltip-top">
						<div class="title-and-bezirk">
							<div class="lor">Straße</div>
							<div class="title">
								{@html region.name.replace("/", "/&#8203;")}
							</div>
							<div class="bezirk">
								in {(typeof region.bezirke === "string" ? JSON.parse(region.bezirke) : region.bezirke).join(", ")}
							</div>
						</div>
						{#if isSelected}
							<div class="button-close" on:click={closeClick} />
						{/if}
					</div>
					<div class="rule" />
					<div class="row rowSelected" style="margin-top: 15px;">
						<span class="label">Parkplätze pro 100m:</span><br />
						<span class="value value-box" style:background={choroplethColor} style:color={choroplethColorText}>
							{frmtF(region.carsPerLength * 100)}
							{#if choroplethColorText.startsWith("#f")}
								<img src="{PATH}assets/images/icons/car-w.svg" alt="" />
							{:else}
								<img src="{PATH}assets/images/icons/car.svg" alt="" />
							{/if}
						</span>
					</div>

					<div class="row">
						<span class="label">Anzahl der Parkplätze:</span>
						<span class="value">{frmtI(region["cars"])}</span>
					</div>
				{/if}
			</div>
		{/if}

		<div class="legend legend-mobile">
			<div class="legend-header">
				<div class="legend-title">{optionsById[selectedOption].legendLabel}</div>
				<InfoBox content={infoboxText} />
			</div>
			<div class="legend-items {optionsById[selectedOption].legendType}">
				{#if optionsById[selectedOption].legendType === "separate"}
					{#each optionsById[selectedOption].choroplethValues as v}
						<div class="legend-item">
							<div class="legend-symbol" style:background={v.color} />
							<div class="legend-label">{v.value}</div>
						</div>
					{/each}
				{/if}
				{#if optionsById[selectedOption].legendType === "continuous-bins"}
					{@const bins =
						optionsById[selectedOption].id === "streets"
							? optionsById[selectedOption].colors.scale.domain()
							: [
									optionsById[selectedOption].colors.scale.domain()[0],
									...optionsById[selectedOption].colors.scale.thresholds(),
							  ]}
					{#each bins as v, i}
						{@const color = optionsById[selectedOption].colors.scale(v)}
						<div class="legend-item">
							<div class="legend-symbol" style:background={color} />
							<div class="legend-label">
								{#if optionsById[selectedOption].choroplethValues[i].label !== undefined}
									{optionsById[selectedOption].choroplethValues[i].label}
								{:else}
									{Math.round(v)}{i === bins.length - 1 ? "%" : ""}
								{/if}
							</div>
						</div>
					{/each}
				{/if}
			</div>
		</div>
	</div>
	<div class="attribution">
		Daten:&nbsp;<a href="https://www.xtransform.org/" target="_blank">Parkplatz Transform</a>,
		<a href="https://github.com/hanshack/parkplatztransform-data-analysis" target="_blank">Hans Hack</a>,
		<a href="https://www.berlin.de/sen/sbw/stadtdaten/geoportal/liegenschaftskataster/alkis/" target="_blank"
			>ALKIS (Grünanlagen)</a
		>,
		<a href="https://www.statistik-berlin-brandenburg.de/" target="_blank">Amt für Statistik Berlin-Brandenburg</a>,
		<a href="https://www.berlin.de/sen/sbw/stadtdaten/geoportal/" target="_blank">Geoportal Berlin</a>
		Kartenbasis:&nbsp;<a href="https://www.openmaptiles.org/" target="_blank">&copy;&nbsp;OpenMapTiles</a>
		<a href="https://www.openstreetmap.org/copyright" target="_blank">&copy;&nbsp;OpenStreetMap contributors</a>
		{#if showLuftbilder}
			| <a
				target="_blank"
				href="https://fbinter.stadt-berlin.de/fb/berlin/service.jsp?id=a_luftbild2022_true_rgbi@senstadt&amp;type=FEED"
				>Geoportal Berlin / Digitale farbige Orthophotos 2022 (DOP20RGBI)</a
			>
		{/if}
	</div>

	{#if paywall}
		<div class="pw-overlay" />
	{/if}
</div>

<style lang="scss">
	@import "./main.scss";

	$tooltip-offset: 25vh;

	.gruenflaechen-map {
		font-family: $ff-sans;
		font-size: $fs-base;
		line-height: $lh-base;
		color: $color;
		user-select: none;
	}

	.map-container {
		position: relative;
		margin-bottom: 0;
		transition: margin 0.3s ease;
	}

	.attribution {
		font-size: 12px;
		color: #666;
		line-height: 1.5em;
		margin-top: 12px;
		text-align: right;

		@media (max-width: $breakpoint-mobile-l) {
			text-align: center;
		}

		a {
			text-decoration: none;
		}
	}

	.search {
		flex: 0 0 265px;
	}
	.map {
		position: relative;
		height: 650px;
	}

	.ui-container-top {
		display: flex;
		justify-content: space-between;
		margin-bottom: 5px;
		align-items: center;
		opacity: 1;
		transition: opacity 0.4s;

		&.inactive {
			pointer-events: none;
			opacity: 0.8;
		}

		@media (max-width: $breakpoint-tablet-l) {
			margin-bottom: 5px;
			flex-wrap: wrap;

			.legend {
				order: 1;
			}

			.buttons {
				order: 2;
			}

			.search {
				order: 0;
				flex: 0 0 100%;
				margin-bottom: 8px;
			}
		}

		@media (max-width: $breakpoint-mobile-l) {
			margin-bottom: 10px;

			.search {
				width: 100%;
				margin: 10px auto;

				:global(.tsg-search) {
					width: 100%;
					margin: 0 auto;
				}
			}

			.buttons {
				text-align: center;
				width: 100%;
				margin: 0 auto;
			}

			.legend {
				display: none;
			}
		}
	}

	.ui-container-bottom {
		display: flex;
		justify-content: space-between;
	}

	.headline {
		font-weight: $bold;
		font-size: 16px;
		text-align: center;
		margin-bottom: 10px;

		@media (min-width: #{$breakpoint-tablet-l + 1px}) {
			display: none;
		}
	}

	// desktop
	@media (min-width: #{$breakpoint-tablet-l + 1px}) {
	}

	// large tablets
	@media (min-width: #{$breakpoint-tablet-s + 1px}) and (max-width: $breakpoint-tablet-l) {
		.map {
			height: 600px;
		}
	}

	// small tablets
	@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-s) {
		.map {
			height: 600px;
		}
	}

	// large/normal phones
	@media (min-width: #{$breakpoint-mobile-m + 1px}) and (max-width: $breakpoint-mobile-l) {
		.map {
			height: 460px;
		}
	}

	// small phones
	@media (min-width: #{$breakpoint-mobile-s + 1px}) and (max-width: $breakpoint-mobile-m) {
		.map {
			height: 420px;
		}
	}

	// very small phones
	@media (max-width: $breakpoint-mobile-s) {
		.map {
			height: 420px;
		}
	}

	@media (max-width: 600px) {
	}

	:global(.mapboxgl-ctrl-scale) {
		bottom: -4px !important;
	}

	:global(.marker) {
		pointer-events: none;
		background-position: center bottom;
		background-size: contain;
		background-repeat: no-repeat;
		position: absolute;
		bottom: 0;
		left: 0;
		transform: translateX(-50%);
		font-weight: $bold;
		font-size: 11px;
		font-family: $ff-sans;
		line-height: 14px;
		color: #fff;
		text-shadow: 1px 1px #1f1f1f, -1px 1px #1f1f1f, -1px -1px #1f1f1f, 1px -1px #1f1f1f, 0 1px #1f1f1f, 0 -1px #1f1f1f,
			1px 0 #1f1f1f, -1px 0 #1f1f1f, 0 0 2px rgba(0, 0, 0, 0.1);
	}

	:global(.marker.marker--fernsehturm) {
		background-image: url(../assets/images/berlin/fernsehturm.svg);
		height: 80px;
		width: 59px;
	}

	:global(.ring-marker-text) {
		position: absolute;
		font-family: $ff-sans;
		white-space: nowrap;
		font-size: 14px;
		color: #272727;
		font-weight: $bold;
		text-shadow: 1px 1px #fff, -1px 1px #fff, 1px -1px #fff, -1px -1px #fff;
		transform: translate(-100%, -100%) !important;
		pointer-events: none;
	}

	:global(.hidden .ring-marker-text) {
		display: none;
	}

	:global(.region-popup) {
    pointer-events: none !important;
    margin-top: -20px;
  }

	:global(.region-popup .mapboxgl-popup-content) {
		border-radius: 4px;
		font-family: $ff-sans;
		text-align: center;
		padding: 7px 9px !important;
		pointer-events: none !important;
	}

	:global(.region-popup .type) {
		font-weight: normal;
		font-size: 9px;
		line-height: 13px;
		letter-spacing: 0.085em;
		text-transform: uppercase;
		color: #747474;
	}

	:global(.region-popup .name) {
		font-weight: $bold;
		letter-spacing: 0.015em;
		font-size: 13px;
		line-height: 14px;
	}

	:global(.region-popup .bezirk) {
		letter-spacing: 0.015em;
		font-size: 12px;
		line-height: 15px;
	}

	// very phones
	@media (max-width: $breakpoint-mobile-l) {
		:global(.marker) {
			font-size: 11px;
			line-height: 13px;
		}

		:global(.marker--fernsehturm) {
			height: 36px;
			width: 16px;
		}

		:global(.ring-marker-text) {
			font-size: 11px;
			// display: none;
		}
	}

	// Anpassungen mapcontrols und scale
	// loading spinner

	.map {
		z-index: 0;
	}

	.map-loading {
		position: absolute;
		background: #f2f2f2;
		left: 0;
		top: 0;
		bottom: 0;
		right: 0;
		pointer-events: all;
		z-index: 1;
		transition: opacity 0.2s ease;
		opacity: 1;

		&.mapLoaded {
			opacity: 0;
			pointer-events: none;
		}
	}

	.ts-spinner {
		width: 40px;
		height: 40px;
		position: absolute;
		left: 50%;
		top: 50%;
		transform: translate(-50%, -50%);
	}

	.ts-double-bounce1,
	.ts-double-bounce2 {
		width: 100%;
		height: 100%;
		border-radius: 50%;
		background-color: #333;
		opacity: 0.6;
		position: absolute;
		top: 0;
		left: 0;
		animation: sk-bounce 2s infinite ease-in-out;
	}

	.ts-double-bounce2 {
		animation-delay: -1s;
	}

	@keyframes sk-bounce {
		0%,
		100% {
			transform: scale(0);
			-webkit-transform: scale(0);
		}

		50% {
			transform: scale(1);
			-webkit-transform: scale(1);
		}
	}

	// LEGENDE

	.legend {
		width: 265px;
		font-size: 13px;
		line-height: 20px;
		white-space: nowrap;
		// padding: 5px 10px;
		display: flex;
		flex-direction: column;
		// background: rgba(255, 255, 255, 0.9);
		// position: absolute;
		top: 6px;
		left: 10px;
		border-radius: 3px;
		color: #787878;
		text-shadow: -1px 1px 0 $white, 1px 1px 0 $white, 1px -1px 0 $white, -1px -1px 0 $white;
		padding-bottom: 20px;

		@media (min-width: #{$breakpoint-mobile-l + 1px}) {
			&.invert-colors {
				color: $white;
				text-shadow: -1px 1px 0 $black, 1px 1px 0 $black, 1px -1px 0 $black, -1px -1px 0 $black;

				.legend-symbol {
					border: 1px solid $black;
				}
			}
		}
	}

	.legend-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.legend.separate {
		.legend-item {
			display: inline-block;
		}
	}

	.legend-items.continuous-bins {
		display: flex;
		width: 100%;
		align-items: flex-end;
		position: relative;
		box-shadow: 0 0 3px rgba(0, 0, 0, 0.25);

		.legend-item {
			display: flex;
			flex-direction: column;
			width: 100%;

			// &:last-child {
			// 	text-align: right;
			// 	position: absolute;
			// 	left: calc(100% - 17px);
			// 	width: unset;

			// 	.legend-label {
			// 		transform: translateX(0);
			// 	}

			// 	@media (max-width: $breakpoint-mobile-m) {
			// 		left: calc(100% - 33px);
			// 	}
			// }

			&:first-child {
				box-shadow: none;

				@media (max-width: $breakpoint-mobile-m) {
					.legend-label {
						transform: translateX(0);
					}
				}
			}
		}

		.legend-label {
			transform: translateX(-50%);
			width: fit-content;
			position: absolute;
			top: 100%;
		}

		// .legend-symbol {
		// 	width: 30px;
		// }
	}

	.legend-title {
		font-weight: 600;
		font-size: 14px;
	}

	.legend-item {
		// display: inline-block;
		box-shadow: -1px 0 0 #fff;

		&:last-child {
			margin-right: 0;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend-title {
			margin-right: 5px;
		}

		.separate .legend-item {
			margin-right: 5px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.legend-title {
			margin-right: 10px;
		}

		.separate .legend-item {
			margin-right: 10px;
		}
	}

	.legend-symbol {
		display: inline-block;
		// width: 8px;
		height: 8px;
		// box-shadow: 0 0 2px rgba(0, 0, 0, 0.25);
		// margin-right: 2px;
	}

	.separate {
		.legend-symbol {
			width: 8px;
			margin-right: 2px;
			border: 1px solid $white;
		}
	}

	.legend-label {
		display: inline-block;
	}

	@media (max-width: $breakpoint-mobile-l) {
		.legend {
			position: relative;
			background: none;
			padding: 10px 0 10px 0;
			left: 0;
			top: 0;
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
		}
	}

	// TOOLTIP

	.tooltip {
		background: white;
		padding: 11px 10px 25px 15px;
		position: absolute;
		top: 100px;
		right: 10px;
		font-size: 14px;
		line-height: 20px;
		border-radius: 3px;
		box-shadow: 0 4px 5px rgba(0, 0, 0, 0.15);
		width: 280px;
		box-sizing: border-box;
		z-index: 1;

		.lor {
			text-transform: uppercase;
			font-size: 11px;
			color: #8b8b8b;
		}
		.rule {
			height: 1px;
			background: #cdcdcd;
			margin: 8px 0;
		}

		.tooltip-top {
			display: flex;
			justify-content: space-between;
		}

		.button-close {
			cursor: pointer;
			background-position: center center;
			background-image: url(../assets/images/icons/icon_close.svg);
			background-repeat: no-repeat;
			width: 20px;
			height: 20px;
			background-size: contain;
			margin-right: 5px;
		}

		.type {
			color: #8b8b8b;
			font-weight: 400;
			font-size: 11px;
			line-height: 11px;
			letter-spacing: 1px;
			text-transform: uppercase;
			margin-bottom: 3px;
		}

		.title {
			font-weight: 600;
			font-size: 18px;
			line-height: 21px;
			margin-bottom: 5px;
		}

		.bezirk {
			line-height: 12px;
			margin-bottom: 6px;
		}

		.value {
			font-weight: 600;
		}

		.value-box {
			padding: 0 6px;
			border-radius: 3px;
			display: inline-block;
		}

		.social {
			color: #8b8b8b;
			line-height: 14px;
		}

		.flaeche {
			:global(b) {
				color: #6b6b6b;
				font-weight: 600;
			}
		}

		.icon-label {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
			opacity: 0.5;
		}

		.icon-value {
			display: inline-block;
			width: 14px;
			height: 14px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: contain;
			position: relative;
			top: 3px;
		}

		.icon-bad {
			display: inline-block;
			width: 16px;
			height: 16px;
			background-position: center center;
			background-repeat: no-repeat;
			background-size: 12px;
			position: relative;
			top: 3px;
			background-color: #fcece9;
			border-radius: 50%;
			margin: -1px 1px;
		}

		.row {
			white-space: normal;

			.value img {
				position: relative;
				top: 1px;
				left: 1px;
			}
		}

		.rowSelected {
			.label {
				font-weight: 600;
			}

			.icon-label {
				opacity: 1;
			}
		}

		.row-text {
			white-space: normal;
			color: #2a2a2a;

			b {
				font-weight: 600;
				color: #6a6a6a;
			}
		}

		@media (max-width: $breakpoint-mobile-l) {
			// position: relative;
			// top: -$tooltip-offset;
			position: absolute;
			top: calc(100% - #{$tooltip-offset});
			width: 100%;
			left: 0;
			right: 0;
			border-radius: 10px;
			box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
			padding: 10px 15px;

			.title,
			.bezirk {
				display: inline;
			}

			.button-close {
				margin-right: 0px;
			}

			.title-and-bezirk {
				position: relative;
				padding-right: 20px;
				margin-bottom: 8px;
			}

			&:not(.expanded) {
				.rule,
				.flaeche,
				.social,
				.row {
					display: none;
				}

				.rowSelected {
					display: block;

					.label {
						font-weight: normal;
					}
				}
			}
		}
	}

	$tooltip-height-factor: 0.55;

	@media (min-width: 501px) and (max-width: $breakpoint-mobile-l) {
		$tooltip-height: 60vw;

		.tooltip {
			&.plr {
				height: $tooltip-height;
			}
			&.streets {
				height: $tooltip-height-factor * $tooltip-height;
			}
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.9 * #{$tooltip-offset});
		}
	}

	@media (min-width: 481px) and (max-width: 500px) {
		$tooltip-height: 67vw;

		.tooltip {
			&.plr {
				height: $tooltip-height;
			}
			&.streets {
				height: $tooltip-height-factor * $tooltip-height;
			}
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.9 * #{$tooltip-offset});
		}
	}

	@media (min-width: 441px) and (max-width: 480px) {
		$tooltip-height: 73vw;

		.tooltip {
			&.plr {
				height: $tooltip-height;
			}
			&.streets {
				height: $tooltip-height-factor * #{$tooltip-height};
			}
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.9 * #{$tooltip-offset});
		}
	}

	@media (min-width: 401px) and (max-width: 440px) {
		$tooltip-height: 81vw;

		.tooltip {
			&.plr {
				height: $tooltip-height;
			}
			&.streets {
				height: $tooltip-height-factor * #{$tooltip-height};
			}
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.9 * #{$tooltip-offset});
		}
	}

	@media (min-width: 341px) and (max-width: 400px) {
		$tooltip-height: 106vw;

		.tooltip {
			&.plr {
				height: $tooltip-height;
			}
			&.streets {
				height: $tooltip-height-factor * #{$tooltip-height};
			}
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.9 * #{$tooltip-offset});
		}
	}

	@media (max-width: 340px) {
		$tooltip-height: 115vw;

		.tooltip {
			&.plr {
				height: $tooltip-height;
			}
			&.streets {
				height: $tooltip-height-factor * #{$tooltip-height};
			}
		}
		.map-container.tooltip-shown {
			margin-bottom: calc(#{$tooltip-height} - 0.9 * #{$tooltip-offset});
		}
	}

	// LUFTBILDER BUTTON

	.button-luftbilder {
		width: 48px;
		height: 48px;
		background: #fff;
		border-radius: 4px;
		box-shadow: 0 3px 5px rgba(0, 0, 0, 0.25);
		position: absolute;
		top: 20px;
		right: 20px;

		&.hidden {
			display: none;
		}
	}

	.button-luftbilder-image {
		position: absolute;
		left: 4px;
		top: 4px;
		width: calc(100% - 8px);
		height: calc(100% - 8px);
		border-radius: 3px;
		background-size: cover;
		background-position: center center;
		overflow: hidden;
	}

	.button-luftbilder-image-on {
		display: none;
		background-image: url(../assets/images/icons/icon_data_layer.jpg);
	}

	.button-luftbilder-image-off {
		background-image: url(../assets/images/icons/icon_satellite_layer.jpg);
		display: block;
	}

	.button-luftbilder.luftbilder {
		.button-luftbilder-image-on {
			display: block;
		}

		.button-luftbilder-image-off {
			display: none;
		}
	}

	.button-luftbilder-text {
		color: #6a6a6a;
		font-weight: 600;
		font-size: 11px;
		line-height: 12px;
		text-align: center;
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translate(-50%, 5px);
		text-shadow: -1px 1px 0 #fff, 1px 1px 0 #fff, 1px -1px 0 #fff, -1px -1px 0 #fff;

		&.desktop {
			width: 84px;
		}
	}

	@media (min-width: #{$breakpoint-mobile-l + 1px}) {
		.button-luftbilder-text.mobile {
			display: none;
		}
	}

	@media (max-width: $breakpoint-mobile-l) {
		.button-luftbilder {
			width: 38px;
			height: 38px;
			top: 10px;
			right: 10px;
		}

		.button-luftbilder-image {
			left: 3px;
			top: 3px;
			width: calc(100% - 6px);
			height: calc(100% - 6px);
		}

		.button-luftbilder-text.desktop {
			display: none;
		}
	}

	// buttons
	.button-main {
		font-weight: 500;
		text-align: center;
		display: flex;
		align-items: center;
		color: #787878;

		img {
			height: 26px;
			margin-right: 5px;
			width: unset;

			@media (max-width: $breakpoint-mobile-l) {
				margin-right: 1px;
			}
		}

		span::after {
			content: attr(data-title);
			font-weight: 600;
			display: block;
			height: 0px;
			visibility: hidden;
		}

		&.active {
			font-weight: 600;
			color: #2a2a2a;
		}

		.button-m {
			display: none;
		}

		@media (max-width: $breakpoint-mobile-l) {
			.button-d {
				display: none;
			}

			.button-m {
				display: inline;
			}
		}
	}

	// Paywall

	.pw-overlay {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background: transparent;
	}

	.pw-map-image {
		position: absolute;
		left: 0;
		right: 0;
		bottom: 0;
		top: 0;
		background-image: url(../assets/images/pw-map-image.jpg);
		background-size: cover;
		background-repeat: no-repeat;
		background-position: right center;
		filter: blur(2px);

		@media (min-width: #{$breakpoint-mobile-l + 1px}) and (max-width: $breakpoint-tablet-l) {
			background-position: right top;
			background-image: url(../assets/images/pw-map-image-m.jpg);
		}

		@media (max-width: $breakpoint-mobile-l) {
			background-position: center center;
			background-image: url(../assets/images/pw-map-image-s.jpg);
		}
	}
	@media (max-width: $breakpoint-mobile-l) {
		.pw {
			.map {
				height: 144vw !important;
			}
			.legend {
				display: none;
			}
		}
	}

	.legend-mobile {
		display: none;

		@media (max-width: $breakpoint-mobile-l) {
			display: block;
			margin: 0 auto;
			padding-bottom: 20px;
		}
	}
</style>
